/**
 * /diagnostic/results — the Results tab.
 *
 * Two pages behind one route. An organisation the roadmap answers (the `prime_score_roadmap`
 * flag on and `roadmap_can_read`) gets the Prime Score page. Everyone else keeps the diagnostic
 * page: the flag off, and Free organisations, whom the diagnostic wizard still sends here when
 * they finish.
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { formatDateNumeric } from '@/lib/dates';
import { useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Loader2, AlertTriangle, Download, Share2 } from 'lucide-react';
import { useOrganization } from '@/contexts';
import {
  useCalculateBSS,
  useLatestBSSCalculation,
  useBSSCalculationHistory,
} from '@/hooks/use-bss-calculator';
import { useScoreView, trendPointsFor } from '@/hooks/use-score-view';
import { useRoadmapAccess } from '@/hooks/use-roadmap';
import { isRoadmapEnabled, loadFeatureFlags } from '@/lib/feature-flags';
import { isPillarCode, type PillarCode } from '@/lib/diagnostic/pillar-order';
import { DiagnosticAccessGate } from '@/components/diagnostic/DiagnosticAccessGate';
import { useDiagnosticAccess } from '@/api/diagnostic-access';
import { UpgradePrompt } from '@/components/upgrade/UpgradePrompt';
import { BssScoreCard } from '@/components/results/BssScoreCard';
import { PillarRadarChart } from '@/components/results/PillarRadarChart';
import { LifecycleStageCard } from '@/components/results/LifecycleStageCard';
import { TrendChart } from '@/components/results/TrendChart';
import { useResultsActions } from '@/components/results/results-actions';
import { PrimeResultsPage, ResultsSkeleton } from '@/components/results/prime/PrimeResultsPage';
import { DiagnosticViewTabs } from '@/components/roadmap/DiagnosticViewTabs';

type ResultsSearch = {
  sessionId?: string;
  pillar?: PillarCode;
};

export const Route = createFileRoute('/diagnostic/results')({
  // Which page this is depends on the roadmap flag: re-await the (cached) flag query so a cold
  // cache never shows a flag-on organisation the diagnostic page first.
  beforeLoad: async ({ context }) => {
    await loadFeatureFlags(context.queryClient);
  },
  component: ResultsRoute,
  // `undefined`, never `null`, for a missing or invalid param: TanStack writes `null` into the
  // URL as the string "null", which reads back as a session id.
  validateSearch: (search: Record<string, unknown>): ResultsSearch => {
    const raw = typeof search.sessionId === 'string' ? search.sessionId.trim() : '';
    return {
      sessionId: raw === '' || raw === 'null' || raw === 'undefined' ? undefined : raw,
      pillar: isPillarCode(search.pillar) ? search.pillar : undefined,
    };
  },
});

function ResultsRoute() {
  const { sessionId, pillar } = Route.useSearch();
  if (!isRoadmapEnabled()) return <GatedResultsPage />;
  return <PrimeOrDiagnosticResults sessionId={sessionId ?? null} pillar={pillar ?? null} />;
}

/**
 * The roadmap's own access read decides. A failed read falls back to the diagnostic page,
 * which has its own gate.
 */
function PrimeOrDiagnosticResults({ sessionId, pillar }: { sessionId: string | null; pillar: PillarCode | null }) {
  const { organization } = useOrganization();
  const access = useRoadmapAccess(organization?.id ?? null);

  if (!organization || access.isLoading) {
    return (
      <div className="container mx-auto max-w-5xl space-y-6 px-4 py-6">
        <DiagnosticViewTabs />
        <ResultsSkeleton />
      </div>
    );
  }
  if (access.data?.canRead) return <PrimeResultsPage sessionId={sessionId} pillar={pillar} />;
  return <GatedResultsPage />;
}

function GatedResultsPage() {
  return (
    <DiagnosticAccessGate>
      <ResultsPage />
    </DiagnosticAccessGate>
  );
}

function ResultsPage() {
  const navigate = useNavigate();
  const { sessionId, pillar } = Route.useSearch();
  const { organization } = useOrganization();
  // A Free org gets ONE diagnostic; paid plans are unlimited (migration 349, the
  // RLS insert policy enforces it server-side regardless). Wait for the access read
  // to resolve before deciding between Retake and the upgrade prompt, so neither
  // flashes on first paint. Lapsed orgs never reach here — DiagnosticAccessGate
  // (allowed=false) paywalls them first — so on this page !canStart means a Free
  // org that has spent its one run.
  const { data: diagnosticAccess } = useDiagnosticAccess(organization?.id);
  const accessResolved = diagnosticAccess !== undefined;
  const canRetake = diagnosticAccess?.canStart ?? false;

  const { mutate: calculateBSS, isPending: calculating } = useCalculateBSS();
  const { data: calculation, isLoading: loadingCalculation } = useLatestBSSCalculation(
    organization?.id || null
  );
  const { data: history = [] } = useBSSCalculationHistory(organization?.id || null, 30);
  // The one score this page shows (ticket 86d4baew6): the Prime Score behind the flag, the
  // diagnostic score otherwise. Everything below reads the view, never the calculation, for a
  // number on screen. The calculation is still read for the facts only it holds: guardrails,
  // leadership, innovation, the decline index, and whether a retake is in progress.
  const view = useScoreView(organization?.id || null);
  const trendPoints = trendPointsFor(view, history);

  // Capture ref wraps everything that should be in the shared image — the
  // action buttons live outside it so they're not screenshotted.
  const captureRef = useRef<HTMLDivElement>(null);
  const actions = useResultsActions({
    organizationId: organization?.id ?? null,
    captureRef,
    caption:
      view.scored && view.score !== null
        ? `My Prime Score: ${view.score} / ${view.max}${view.bandLabel ? ` · ${view.bandLabel}` : ''}`
        : '',
    score: view.score,
    canShare: !!calculation,
  });
  const topTier = actions.topTier;

  // Auto-trigger calculation if sessionId provided and no calculation exists. Once per session:
  // a failing calculation must not be retried in a loop.
  const calculatedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!sessionId || !organization || calculation || calculating || loadingCalculation) return;
    if (calculatedFor.current === sessionId) return;
    calculatedFor.current = sessionId;
    calculateBSS({
      sessionId,
      organizationId: organization.id,
    });
  }, [sessionId, organization, calculation, calculating, loadingCalculation, calculateBSS]);

  // Deep-link scroll to a specific pillar card
  useEffect(() => {
    if (!pillar || !calculation) return;
    // Delay one frame so the card is in the DOM before we scroll
    const id = requestAnimationFrame(() => {
      const el = document.getElementById(`pillar-card-${pillar}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
    return () => cancelAnimationFrame(id);
  }, [pillar, calculation]);

  if (calculating || loadingCalculation) {
    return (
      <div className="container mx-auto px-4 py-16">
        <Card className="max-w-2xl mx-auto">
          <CardContent className="py-16 text-center space-y-4">
            <Loader2 className="w-16 h-16 animate-spin mx-auto text-primary" />
            <h2 className="text-2xl font-bold">Calculating Your Prime Score...</h2>
            <p className="text-muted-foreground">
              Analyzing your responses across 6 business pillars
            </p>
            <div className="max-w-xs mx-auto space-y-2 text-sm text-left text-muted-foreground">
              <p>✓ Scoring 104 questions</p>
              <p>✓ Calculating pillar scores</p>
              <p>✓ Applying trend modifiers</p>
              <p>✓ Evaluating guardrails</p>
              <p>✓ Determining lifecycle stage</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!calculation) {
    return (
      <div className="container mx-auto px-4 py-16">
        <Card className="max-w-2xl mx-auto">
          <CardContent className="py-16 text-center space-y-4">
            <AlertTriangle className="w-16 h-16 mx-auto text-yellow-500" />
            <h2 className="text-2xl font-bold">No Calculation Available</h2>
            <p className="text-muted-foreground">
              Complete the diagnostic assessment to see your Prime Score
            </p>
            <Button onClick={() => navigate({ to: '/diagnostic', search: { sessionId: undefined } })}>
              Start Diagnostic
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <div ref={captureRef} className="max-w-6xl mx-auto space-y-8">
        {/* F-131: Results | Roadmap (| History) — hidden from print and from the shared image */}
        <DiagnosticViewTabs />
        {/* Header */}
        <div className="text-center space-y-4">
          <h1 className="text-4xl font-bold">Your Business Assessment Results</h1>
          <p className="text-muted-foreground">
            Updated On {formatDateNumeric(calculation.createdAt)}
          </p>

          {/* Action buttons — hidden from print AND from html2canvas capture
              via data-html2canvas-ignore so they don't appear in the shared
              image, while staying in their original position in the UI. */}
          <div
            className="flex justify-center gap-2 print:hidden"
            data-html2canvas-ignore="true"
          >
            <Button variant="outline" size="sm" onClick={() => window.print()}>
              <Download className="w-4 h-4 mr-2" />
              Print or save PDF
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={actions.openShare}
              disabled={!topTier}
              title={topTier ? `Share to ${topTier.tier_name}` : 'Join a community to share'}
            >
              <Share2 className="w-4 h-4 mr-2" />
              Share Results
            </Button>
          </div>
        </div>

        {/* Main Score and Lifecycle Stage */}
        {view.scored && view.score !== null && view.band ? (
          <div className="grid md:grid-cols-2 gap-6">
            <BssScoreCard
              score={view.score}
              max={view.max}
              delta={view.delta}
              // The trend modifier is a diagnostic fact, so it is only ever shown beside the
              // diagnostic number.
              trendModifier={view.source === 'diagnostic' ? calculation.trendModifier : 0}
              lifecycleStage={view.band}
              stageLabel={view.bandLabel}
              animate={true}
            />

            <LifecycleStageCard stage={view.band} max={view.max} />
          </div>
        ) : (
          <Card>
            <CardContent className="py-10 text-center space-y-2">
              <h2 className="text-xl font-bold">Not scored yet</h2>
              <p className="text-muted-foreground">
                Your Prime Score appears here once it has been calculated for your business.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Pillar Visualization: the same 0 to 100 pillar figure in both cards. The radar keeps
            its auto-fit ring, because most orgs score 1 to 10 per pillar and a fixed 0 to 100
            axis draws that as a dot (QA, 15 Sep 2026). The ring says which range it is on. */}
        {view.pillarScores && (
        <div className="grid md:grid-cols-2 gap-6">
          <PillarRadarChart
            pillarScores={view.pillarScores}
            unscoredPillars={view.unscoredPillars}
          />

          {/* Pillar Scores List */}
          <Card>
            <CardContent className="pt-6 space-y-4">
              {Object.entries(view.pillarScores)
                .sort((a, b) => b[1] - a[1])
                .map(([pillarCode, score]) => {
                  const isTarget = pillar === pillarCode;
                  // A pillar the diagnostic never covered has no figure. The view fills the key
                  // with 0 so the radar keeps six axes; printing that 0 here would state a
                  // score for a pillar nobody was asked about.
                  const unscored = view.unscoredPillars.includes(pillarCode as PillarCode);
                  return (
                    <div
                      key={pillarCode}
                      id={`pillar-card-${pillarCode}`}
                      className={`space-y-2 rounded-lg p-2 transition-all ${isTarget ? 'ring-2 ring-primary bg-primary/5' : ''}`}
                    >
                      <div className="flex justify-between items-center">
                        <span className="font-medium capitalize">{pillarCode}</span>
                        {unscored ? (
                          <span className="text-sm font-medium text-muted-foreground">Not scored</span>
                        ) : (
                          <span className="text-lg font-bold">{Math.round(score)}</span>
                        )}
                      </div>
                      <div className="h-2 bg-secondary rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary transition-all"
                          style={{ width: `${unscored ? 0 : score}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
            </CardContent>
          </Card>
        </div>
        )}

        {/* Trend Chart: the roadmap snapshots on the Prime path, the calculation history
            otherwise, on the y axis that number is scored against. */}
        {trendPoints.length > 1 && <TrendChart points={trendPoints} max={view.max} />}

        {/* Guardrail Violations */}
        {calculation.guardrailViolations && calculation.guardrailViolations.length > 0 && (
          <Card className="border-yellow-500">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-yellow-500 mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-semibold mb-2">Areas Requiring Attention</h3>
                  <ul className="space-y-1">
                    {calculation.guardrailViolations.map((code) => (
                      <li key={code} className="text-sm text-muted-foreground">
                        • {code.replace(/_/g, ' ')}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Special Scores */}
        <div className="grid md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6 text-center">
              <div className="text-sm text-muted-foreground mb-1">Leadership Score</div>
              <div className="text-4xl font-bold">{Math.round(calculation.leadershipScore)}</div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6 text-center">
              <div className="text-sm text-muted-foreground mb-1">Innovation Score</div>
              <div className="text-4xl font-bold">{Math.round(calculation.innovationScore)}</div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6 text-center">
              <div className="text-sm text-muted-foreground mb-1">Decline Index</div>
              {/* Null when there are no trend questions to measure it from. Math.round(null) is
                  0, and 0 is the best reading on this scale, so it must not fall through. */}
              <div className="text-4xl font-bold">
                {calculation.declineIndex === null ? '—' : Math.round(calculation.declineIndex)}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Actions — hidden from print AND from html2canvas capture */}
        <div
          className="flex justify-center gap-4 pt-4 print:hidden"
          data-html2canvas-ignore="true"
        >
          <Button variant="outline" onClick={() => navigate({ to: '/' })} size="lg">
            View Dashboard
          </Button>
          {accessResolved && canRetake && (
            <Button onClick={actions.openRetake} size="lg">
              Retake Assessment
            </Button>
          )}
        </div>

        {/* Free orgs spend their one diagnostic on the first run. Instead of a
            missing button, frame re-running as a paid benefit: tracking the score
            over time. Gated on accessResolved so it never flashes before we know,
            and only ever shows a Free org (paid plans keep canRetake). */}
        {accessResolved && !canRetake && (
          <div className="pt-6 print:hidden" data-html2canvas-ignore="true">
            <UpgradePrompt
              className="mx-auto max-w-xl"
              title="Watch your score climb"
              body="Your Prime Score is today's snapshot. On a paid plan you can re-run the diagnostic whenever you want and see how every change moves the number."
              ctaLabel="See plans"
            />
          </div>
        )}
      </div>

      {actions.dialogs}
    </div>
  );
}
